import { useEffect, useState } from 'react';
import { GAME_DEFINITIONS, type InvitePreview, type ListMyServersResponse, type MeResponse, type ServerView, type WebConfig } from '@hearth/shared';
import { useAuth } from '../auth/AuthProvider';
import { linkTo, navigate } from '../router';
import { Campfire } from '../ui/Campfire';
import { displayName, Shell } from './Shell';
import { errorText } from './serverWords';

// /join/{code}: a friend opens an invite link (M10.11), usually on a phone. One card: who invited
// them to which server; signed out, Google or Discord sign-in brings them back here; signed in,
// Join, straight into the server (as is anyone signed in who is already on it). A link that no
// longer works says so.

/** What the link invites to: the public preview (M10.9), read without a token (there may be none). */
export async function fetchPreview(config: WebConfig, code: string, doFetch = globalThis.fetch): Promise<InvitePreview | 'gone'> {
  const res = await doFetch(`${config.apiUrl}/v1/invites/${encodeURIComponent(code)}`);
  if (res.status === 404) return 'gone';
  if (!res.ok) throw new Error(`Hearth answered ${res.status}`);
  return (await res.json()) as InvitePreview;
}

export function Join({ code }: { code: string }) {
  const { status, config, api, signIn, signOut } = useAuth();
  const [preview, setPreview] = useState<InvitePreview | 'gone'>();
  const [me, setMe] = useState<MeResponse>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toServer = (serverId: string) => navigate(`/app/servers/${encodeURIComponent(serverId)}`, { replace: true });

  useEffect(() => {
    if (!config || status === 'loading') return;
    fetchPreview(config, code).then(async (found) => {
      // Already on it: straight to the server. Anyone else sees the invite.
      if (found !== 'gone' && status === 'signed-in' && api) {
        const mine = await api.get<ListMyServersResponse>('/v1/servers').then((r) => r.servers, () => []);
        if (mine.some((s) => s.serverId === found.serverId)) {
          toServer(found.serverId);
          return;
        }
      }
      setPreview(found);
    }, () => setError("Hearth can't be reached right now. Please try again in a moment."));
  }, [config, code, status, api]);

  // Signed in: who, so someone on a shared device sees whose account would join ("not you?").
  useEffect(() => {
    if (status === 'signed-in' && api) api.get<MeResponse>('/v1/me').then(setMe, () => undefined);
  }, [status, api]);

  async function join() {
    if (!api) return;
    setBusy(true);
    try {
      toServer((await api.post<ServerView>(`/v1/invites/${encodeURIComponent(code)}/accept`)).serverId);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Shell me={me} api={api} onSignOut={signOut}>
      <JoinView
        preview={preview}
        signedIn={status === 'signed-in'}
        who={me && displayName(me)}
        busy={busy}
        error={error}
        onSignIn={(provider) => void signIn({ returnTo: `/join/${encodeURIComponent(code)}`, provider })}
        onJoin={() => void join()}
      />
    </Shell>
  );
}

/** "Tarik invited you to play on", or a friend when their sign-in gave no name. */
export const invitedLine = (p: InvitePreview) => `${p.invitedBy.name ?? 'A friend'} invited you to play on`;
/** "Minecraft: Java Edition 26.3 · 3 people play here". */
export const aboutLine = (p: InvitePreview) =>
  `${GAME_DEFINITIONS[p.game].displayName} ${p.version} · ${p.people === 1 ? '1 person plays' : `${p.people} people play`} here`;

/** The join card, given what's known (no fetching: easy to test). `preview` undefined = loading. */
export function JoinView(props: {
  preview: InvitePreview | 'gone' | undefined;
  signedIn: boolean;
  /** Whose account would join, once known. */
  who?: string | undefined;
  busy: boolean;
  error: string;
  onSignIn: (provider: 'Google' | 'Discord') => void;
  onJoin: () => void;
}) {
  const { preview, signedIn, who, busy, error, onSignIn, onJoin } = props;
  const gone = preview === 'gone';
  return (
    <section className="join-card">
      <span className="join-fire"><Campfire state={gone ? 'embers' : 'lit'} height={70} /></span>
      {error && <p className="notice" role="alert">{error}</p>}
      {!preview ? (
        !error && <p className="quiet" aria-live="polite">Loading…</p>
      ) : gone ? (
        <>
          <h1>This invite doesn't work any longer</h1>
          <p className="quiet">It may have expired, or been replaced or turned off. Ask whoever sent it for a new link.</p>
          <a className="btn" {...linkTo('/app')}>Go to your servers</a>
        </>
      ) : (
        <>
          <div className="join-what">
            <p className="quiet">{invitedLine(preview)}</p>
            <h1>{preview.serverName}</h1>
            <p className="quiet">{aboutLine(preview)}</p>
          </div>
          {signedIn ? (
            <div className="join-signin">
              <button className="btn btn-primary btn-tall" type="button" disabled={busy} onClick={onJoin}>
                {busy ? 'Joining…' : `Join ${preview.serverName}`}
              </button>
              {who && <p className="quiet">{`Signed in as ${who}. Not you? Sign out at the top.`}</p>}
            </div>
          ) : (
            <div className="join-signin">
              <button className="btn btn-tall" type="button" onClick={() => onSignIn('Google')}>Continue with Google</button>
              <button className="btn btn-tall" type="button" onClick={() => onSignIn('Discord')}>Continue with Discord</button>
              <p className="quiet">New to Hearth? Signing in is all it takes, and playing on a friend's server costs you nothing.</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
